// @ts-nocheck
/**
 * カード分解システム
 *
 * 責務:
 * - カードを素材に分解
 * - レアリティ・属性・カテゴリに応じた素材生成
 * - 市場価格との連動
 */

import {
  DECOMPOSITION_TABLE,
  CATEGORY_MATERIAL_MAP,
  ATTRIBUTE_MATERIAL_MAP,
  RARITY_FRAGMENT_MAP,
  MARKET_BONUS_THRESHOLDS,
  BASE_CARD_VALUES,
  getDecompositionYield,
  getCategoryMaterial,
  getAttributeMaterial,
  getRarityFragment,
  calculateMarketBonus,
  getBaseCardValue,
} from './data/decompositionTable';

/**
 * カード分解クラス
 */
class Decomposer {
  /**
   * @param {Object} alchemyEngine - 親のAlchemyEngineインスタンス
   */
  constructor(alchemyEngine) {
    this.engine = alchemyEngine;
  }

  /**
   * カードを分解
   *
   * @param {Object} card - 分解するカード
   * @param {string} rarity - カードのレアリティ
   * @param {Object} options - オプション
   * @param {number} options.marketValue - 市場価格（省略時は基本価値を使用）
   * @returns {{ success: boolean, materials: Object[], message: string }}
   */
  decompose(card, rarity, options = {}) {
    // 分解テーブルから基本素材量を取得
    const baseYield = getDecompositionYield(rarity);
    if (!baseYield) {
      return {
        success: false,
        materials: [],
        message: `無効なレアリティ: ${rarity}`,
      };
    }

    // カードが分解可能か確認
    if (card.decomposable === false) {
      return {
        success: false,
        materials: [],
        message: `${card.name}は分解できません`,
      };
    }

    const materials = [];

    // 1. 属性素材
    if (card.attribute && card.attribute !== 'なし') {
      const attributeMaterial = getAttributeMaterial(card.attribute);
      if (attributeMaterial) {
        const count = baseYield.attributeMaterial;
        materials.push({
          type: attributeMaterial,
          count,
          source: '属性素材',
        });
        this.engine.addMaterial(attributeMaterial, count);
      }
    }

    // 2. カテゴリ素材
    if (card.category) {
      const categoryMaterial = getCategoryMaterial(card.category);
      if (categoryMaterial) {
        const count = baseYield.categoryMaterial;
        materials.push({
          type: categoryMaterial,
          count,
          source: 'カテゴリ素材',
        });
        this.engine.addMaterial(categoryMaterial, count);
      }
    }

    // 3. 汎用素材（魔力の核）
    const genericCount = baseYield.genericMaterial;
    materials.push({
      type: '魔力の核',
      count: genericCount,
      source: '汎用素材',
    });
    this.engine.addMaterial('魔力の核', genericCount);

    // 4. レアリティ破片
    const fragmentType = getRarityFragment(rarity);
    if (fragmentType) {
      const fragmentCount = baseYield.rarityFragment;
      materials.push({
        type: fragmentType,
        count: fragmentCount,
        source: 'レアリティ破片',
      });
      this.engine.addMaterial(fragmentType, fragmentCount);
    }

    // 5. 市場価格ボーナス（高価なカードは追加素材）
    const marketValue = options.marketValue || getBaseCardValue(rarity);
    const marketBonus = calculateMarketBonus(marketValue);
    if (marketBonus && marketBonus.count > 0) {
      materials.push({
        type: marketBonus.material,
        count: marketBonus.count,
        source: '市場価格ボーナス',
      });
      this.engine.addMaterial(marketBonus.material, marketBonus.count);
    }

    // 分解履歴に記録
    this._recordDecomposition(card, rarity, materials);

    return {
      success: true,
      materials,
      totalValue: this._calculateTotalValue(materials),
      message: `${card.name}（${rarity}）を分解しました`,
    };
  }

  /**
   * 複数カードを一括分解
   *
   * @param {Array<{ card: Object, rarity: string }>} cards - 分解するカード配列
   * @param {Object} options - オプション
   * @returns {{ success: boolean, results: Object[], totalMaterials: Object, message: string }}
   */
  decomposeMultiple(cards, options = {}) {
    const results = [];
    const totalMaterials = {};

    for (const { card, rarity } of cards) {
      const result = this.decompose(card, rarity, options);
      results.push({
        card,
        rarity,
        ...result,
      });

      // 成功した場合、素材を集計
      if (result.success) {
        for (const material of result.materials) {
          totalMaterials[material.type] =
            (totalMaterials[material.type] || 0) + material.count;
        }
      }
    }

    const successCount = results.filter(r => r.success).length;

    return {
      success: successCount > 0,
      results,
      totalMaterials,
      message: `${cards.length}枚中${successCount}枚を分解しました`,
    };
  }

  /**
   * 分解プレビュー（実際には分解しない）
   *
   * @param {Object} card - プレビューするカード
   * @param {string} rarity - カードのレアリティ
   * @param {Object} options - オプション
   * @returns {{ materials: Object[], totalValue: number }}
   */
  previewDecomposition(card, rarity, options = {}) {
    const baseYield = getDecompositionYield(rarity);
    if (!baseYield) {
      return { materials: [], totalValue: 0 };
    }

    const materials = [];

    // 1. 属性素材
    if (card.attribute && card.attribute !== 'なし') {
      const attributeMaterial = getAttributeMaterial(card.attribute);
      if (attributeMaterial) {
        materials.push({
          type: attributeMaterial,
          count: baseYield.attributeMaterial,
          source: '属性素材',
        });
      }
    }

    // 2. カテゴリ素材
    if (card.category) {
      const categoryMaterial = getCategoryMaterial(card.category);
      if (categoryMaterial) {
        materials.push({
          type: categoryMaterial,
          count: baseYield.categoryMaterial,
          source: 'カテゴリ素材',
        });
      }
    }

    // 3. 汎用素材
    materials.push({
      type: '魔力の核',
      count: baseYield.genericMaterial,
      source: '汎用素材',
    });

    // 4. レアリティ破片
    const fragmentType = getRarityFragment(rarity);
    if (fragmentType) {
      materials.push({
        type: fragmentType,
        count: baseYield.rarityFragment,
        source: 'レアリティ破片',
      });
    }

    // 5. 市場価格ボーナス
    const marketValue = options.marketValue || getBaseCardValue(rarity);
    const marketBonus = calculateMarketBonus(marketValue);
    if (marketBonus && marketBonus.count > 0) {
      materials.push({
        type: marketBonus.material,
        count: marketBonus.count,
        source: '市場価格ボーナス',
      });
    }

    return {
      materials,
      totalValue: this._calculateTotalValue(materials),
    };
  }

  /**
   * 分解効率を計算（市場価格に対する素材価値の割合）
   *
   * @param {Object} card - カード
   * @param {string} rarity - レアリティ
   * @param {number} marketValue - 市場価格
   * @returns {number} 効率（0-1の割合）
   */
  calculateDecompositionEfficiency(card, rarity, marketValue) {
    const preview = this.previewDecomposition(card, rarity, { marketValue });

    // 基本的に市場価格の50%程度の素材価値を想定
    const expectedValue = marketValue * 0.5;
    const actualValue = preview.totalValue;

    return actualValue / expectedValue;
  }

  /**
   * 分解履歴を記録
   * @private
   */
  _recordDecomposition(card, rarity, materials) {
    if (!this.engine.decompositionHistory) {
      this.engine.decompositionHistory = [];
    }

    this.engine.decompositionHistory.push({
      cardId: card.id,
      cardName: card.name,
      rarity,
      materials: materials.map(m => ({ type: m.type, count: m.count })),
      timestamp: Date.now(),
    });

    // 履歴は直近100件のみ保持
    if (this.engine.decompositionHistory.length > 100) {
      this.engine.decompositionHistory.shift();
    }
  }

  /**
   * 素材の合計価値を計算
   * @private
   */
  _calculateTotalValue(materials) {
    // 素材の基本価値（仮の値）
    const materialValues = {
      // 属性結晶
      '炎の結晶': 10,
      '水の結晶': 10,
      '光の結晶': 10,
      '闇の結晶': 10,
      '原始の結晶': 10,
      '未来の結晶': 10,

      // カテゴリ素材
      'ドラゴンの鱗': 30,
      'フェニックスの羽根': 50,
      '人魚の涙': 40,
      '深海の真珠': 60,
      '魔女の秘薬': 35,
      '幽体のエーテル': 25,
      '獣の牙': 15,
      '粘液獣のゼリー': 10,
      '始原の種': 80,
      '魔導石': 25,
      '機械部品': 20,
      '精霊のエッセンス': 40,
      '聖獣の羽根': 45,
      '聖なる光': 50,
      '時空の欠片': 70,
      '鳥の羽根': 15,
      '呪いの残滓': 30,

      // 汎用素材
      '魔力の核': 5,
      '秘宝の欠片': 100,

      // レアリティ破片（実際のシステム: C, UC, R, SR, UR, HR, SEC, ALT, SP, GR）
      'C破片': 5,
      'UC破片': 10,
      'R破片': 20,
      'SR破片': 40,
      'UR破片': 80,
      'HR破片': 150,
      'SEC破片': 300,
      'ALT破片': 500,
      'SP破片': 1000,
      'GR破片': 2000,
    };

    let total = 0;
    for (const material of materials) {
      const unitValue = materialValues[material.type] || 10;
      total += unitValue * material.count;
    }

    return total;
  }

  /**
   * カードが分解可能かチェック
   *
   * @param {Object} card - カード
   * @returns {boolean}
   */
  canDecompose(card) {
    // 明示的に分解不可が設定されている場合
    if (card.decomposable === false) {
      return false;
    }

    // ロックされているカード
    if (card.locked) {
      return false;
    }

    // デッキに入っているカード（オプションでチェック）
    // 実際の実装ではデッキマネージャーと連携

    return true;
  }

  /**
   * 推奨分解カードを取得
   * 同じカードを4枚以上持っている場合など
   *
   * @param {Object[]} collection - コレクション
   * @param {Object} options - オプション
   * @returns {Object[]} 推奨分解カード
   */
  getRecommendedDecompositions(collection, options = {}) {
    const { maxCopies = 4, minRarity = 'C' } = options;
    const recommended = [];

    // カード名＋レアリティでグループ化
    const cardGroups = {};
    for (const card of collection) {
      const key = `${card.cardId}_${card.rarity}`;
      if (!cardGroups[key]) {
        cardGroups[key] = [];
      }
      cardGroups[key].push(card);
    }

    // 4枚以上のカードを分解候補に
    for (const [key, cards] of Object.entries(cardGroups)) {
      if (cards.length > maxCopies) {
        const excess = cards.slice(maxCopies);
        for (const card of excess) {
          recommended.push({
            card,
            reason: `${maxCopies}枚以上所持`,
          });
        }
      }
    }

    return recommended;
  }
}

export default Decomposer;
